import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { format } from 'date-fns';
import { Movie, Screening, Booking } from '../types';

interface BookingConfirmationProps {
  booking: Booking;
  movie: Movie;
  screening: Screening;
  onClose: () => void;
  userEmail: string;
  qrValue?: string;
  isLoadingQr?: boolean;
}

// Badge colours per tier
const TIER_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  silver:  { bg: 'bg-gray-100 dark:bg-gray-700',   text: 'text-gray-700 dark:text-gray-200',  label: '🥈 Silver' },
  gold:    { bg: 'bg-yellow-100 dark:bg-yellow-900', text: 'text-yellow-800 dark:text-yellow-200', label: '🥇 Gold' },
  diamond: { bg: 'bg-blue-100 dark:bg-blue-900',   text: 'text-blue-800 dark:text-blue-200',   label: '💎 Diamond' },
};

export default function BookingConfirmation({ booking, movie, screening, onClose, userEmail, qrValue, isLoadingQr }: BookingConfirmationProps) {
  const hasTicketTypes = booking.ticket_types && booking.ticket_types.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-[95vw] md:max-w-2xl p-8 text-gray-900 dark:text-white max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">Booking Confirmed!</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">{movie.title}</h3>
              <p className="text-gray-700 dark:text-gray-300">{movie.description}</p>
            </div>

            <div className="space-y-2">
              <p className="font-medium">
                Date &amp; Time:
                <span className="ml-2 text-gray-700 dark:text-gray-300">
                  {format(new Date(screening.start_time), 'PPP p')}
                </span>
              </p>
              <p className="font-medium">
                Location:
                <span className="ml-2 text-gray-700 dark:text-gray-300">{screening.location}</span>
              </p>
              <p className="font-medium">
                Seats:
                <span className="ml-2 text-gray-700 dark:text-gray-300">{booking.seats}</span>
              </p>
              <p className="font-medium">
                Total Paid:
                <span className="ml-2 text-indigo-600 dark:text-indigo-400 font-semibold">
                  €{booking.total_amount.toFixed(2)}
                </span>
              </p>
              <p className="font-medium">
                Booking ID:
                <span className="ml-2 text-gray-700 dark:text-gray-300">#{booking.id}</span>
              </p>
            </div>

            {/* ── Seat tier breakdown (only for split-seat bookings) ── */}
            {hasTicketTypes && (
              <div className="space-y-2">
                <p className="font-semibold text-sm uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Seat Breakdown
                </p>
                <div className="flex flex-wrap gap-2">
                  {booking.ticket_types!.map(({ ticket_type, quantity }) => {
                    const style = TIER_STYLES[ticket_type] ?? TIER_STYLES.silver;
                    return (
                      <span
                        key={ticket_type}
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}
                      >
                        {style.label}
                        <span className="ml-1 font-bold">× {quantity}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="bg-indigo-50 dark:bg-indigo-900/40 p-4 rounded-lg">
              <p className="text-sm text-indigo-800 dark:text-indigo-200">
                Please arrive at least 15 minutes before the show. Present this QR code at the entrance.
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900/40 p-6 rounded-lg">
            {isLoadingQr ? (
              <div className="flex flex-col items-center justify-center h-48">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400 mb-4"></div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Generating QR Code...</p>
              </div>
            ) : qrValue ? (
              <>
                <div className="bg-white dark:bg-white p-4 rounded-lg shadow-md">
                  <QRCodeSVG value={qrValue} size={200} level="H" includeMargin={true} />
                </div>
                <p className="mt-4 text-sm text-gray-700 dark:text-gray-300 text-center">Scan this QR code at the venue</p>
              </>
            ) : (
              <p className="text-sm text-red-600 text-center">QR not available yet. Please refresh your bookings and present the QR from your Tickets page.</p>
            )}
          </div>
        </div>

        <div className="mt-8 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 text-gray-900 rounded-md hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
